/**
 * Convert markdown with {{variable}} syntax to HTML with pill markers
 *
 * This function pre-processes markdown strings containing {{variable.key}} placeholders
 * and converts them into HTML with <span data-variable-pill> tags that TipTap can parse.
 *
 * Example:
 *   Input:  "Hello {{customer.name}}, your **{{appointment.service_type}}** is confirmed!"
 *   Output: "Hello <span data-variable-pill data-variable-key=\"customer.name\"></span>, your **<span data-variable-pill data-variable-key=\"appointment.service_type\"></span>** is confirmed!"
 *
 * Then TipTap's Markdown and VariablePill extensions will parse this into proper nodes.
 */

import { VARIABLE_CATEGORIES } from '@/constants/templateVariables';

// Build a Set of all valid variable keys for fast lookup
const VALID_VARIABLE_KEYS = new Set(
  VARIABLE_CATEGORIES.flatMap(category =>
    category.variables.map(v => v.key)
  )
);

/**
 * Check if a variable key is valid
 */
function isValidVariableKey(key: string): boolean {
  return VALID_VARIABLE_KEYS.has(key);
}

/**
 * Convert {{variable}} syntax to HTML pill markers
 *
 * Strategy:
 * 1. Find all {{variable.key}} patterns
 * 2. Replace valid variables with <span data-variable-pill data-variable-key="variable.key"></span>
 * 3. Leave invalid variables as-is (or as plain text)
 * 4. Let TipTap's Markdown extension parse the rest of the markdown
 */
export function convertMarkdownVariablesToHTML(markdown: string): string {
  if (!markdown) return '';

  // Regex to match {{variable.key}}
  const variableRegex = /\{\{([^}]+)\}\}/g;

  return markdown.replace(variableRegex, (match, variableKey) => {
    const trimmedKey = variableKey.trim();

    // Only convert valid variables to pills
    if (isValidVariableKey(trimmedKey)) {
      return `<span data-variable-pill data-variable-key="${trimmedKey}"></span>`;
    }

    // Invalid variable - keep as-is
    return match;
  });
}
